import React, { useEffect, useRef, useState } from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const SCENE_WIDTH = 800;
const SCENE_HEIGHT = 480;

function IRPJourneyScene() {
  const outerRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const update = () => setScale(Math.min(1, el.clientWidth / SCENE_WIDTH));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={outerRef} className="irp-scene-outer px-6">
      <style>{`
        .irp-scene-outer * { margin: 0; padding: 0; box-sizing: border-box; }
        .irp-scene-outer { display: flex; align-items: center; justify-content: center; }
        .irp-scene-outer .scene { width: 800px; height: 480px; position: relative; background: linear-gradient(170deg, #eef2ff 0%, #e0e7ff 50%, #c7d2fe 100%); border-radius: 20px; overflow: hidden; font-family: system-ui, -apple-system, sans-serif; flex-shrink: 0; }

        .irp-scene-outer .cloud { position: absolute; background: white; border-radius: 50px; opacity: 0.6; animation: irpCloudDrift 20s linear infinite; }
        .irp-scene-outer .cloud::before, .irp-scene-outer .cloud::after { content: ''; position: absolute; background: white; border-radius: 50%; }
        .irp-scene-outer .cloud1 { width: 80px; height: 25px; top: 30px; left: -100px; animation-duration: 25s; }
        .irp-scene-outer .cloud1::before { width: 40px; height: 40px; top: -20px; left: 15px; }
        .irp-scene-outer .cloud2 { width: 60px; height: 20px; top: 60px; left: -60px; opacity: 0.4; animation-duration: 30s; animation-delay: 5s; }
        .irp-scene-outer .cloud2::before { width: 30px; height: 30px; top: -15px; left: 10px; }
        @keyframes irpCloudDrift { 0% { transform: translateX(0); } 100% { transform: translateX(1000px); } }

        .irp-scene-outer .path-container { position: absolute; top: 0; left: 0; width: 100%; height: 100%; }
        .irp-scene-outer .path-container svg { width: 100%; height: 100%; }
        .irp-scene-outer .main-path { fill: none; stroke: #c7d2fe; stroke-width: 8; stroke-linecap: round; }
        .irp-scene-outer .main-path-fill { fill: none; stroke: #6366f1; stroke-width: 8; stroke-linecap: round; stroke-dasharray: 1200; stroke-dashoffset: 1200; animation: irpDrawPath 4s ease-out 0.5s forwards; }
        .irp-scene-outer .path-dots { fill: none; stroke: white; stroke-width: 2; stroke-dasharray: 8 12; stroke-linecap: round; opacity: 0.5; }
        @keyframes irpDrawPath { 0% { stroke-dashoffset: 1200; } 100% { stroke-dashoffset: 0; } }

        .irp-scene-outer .milestone { position: absolute; display: flex; flex-direction: column; align-items: center; opacity: 0; }
        .irp-scene-outer .milestone .circle { width: 56px; height: 56px; border-radius: 50%; background: white; display: flex; align-items: center; justify-content: center; flex-direction: column; box-shadow: 0 4px 16px rgba(99,102,241,0.2); border: 3px solid; }
        .irp-scene-outer .milestone .num { font-size: 18px; font-weight: 800; line-height: 1; }
        .irp-scene-outer .milestone .sub { font-size: 7px; font-weight: 600; }

        .irp-scene-outer .m1 { bottom: 280px; left: 60px; animation: irpMilePop 0.6s ease-out 1s both; }
        .irp-scene-outer .m1 .circle { border-color: #c7d2fe; }
        .irp-scene-outer .m1 .num, .irp-scene-outer .m1 .sub { color: #818cf8; }

        .irp-scene-outer .m2 { bottom: 200px; left: 300px; animation: irpMilePop 0.6s ease-out 2s both; }
        .irp-scene-outer .m2 .circle { border-color: #818cf8; }
        .irp-scene-outer .m2 .num, .irp-scene-outer .m2 .sub { color: #6366f1; }

        .irp-scene-outer .m3 { bottom: 280px; left: 540px; animation: irpMilePop 0.6s ease-out 3s both; }
        .irp-scene-outer .m3 .circle { border-color: #6366f1; }
        .irp-scene-outer .m3 .num, .irp-scene-outer .m3 .sub { color: #4f46e5; }

        @keyframes irpMilePop { 0% { opacity: 0; transform: scale(0) translateY(10px); } 60% { transform: scale(1.1) translateY(-3px); } 100% { opacity: 1; transform: scale(1) translateY(0); } }

        .irp-scene-outer .mile-label { position: absolute; background: white; border-radius: 10px; padding: 8px 14px; box-shadow: 0 3px 12px rgba(0,0,0,0.08); text-align: center; opacity: 0; }
        .irp-scene-outer .mile-label .title { font-size: 11px; font-weight: 700; color: #1e1b4b; }
        .irp-scene-outer .mile-label .desc { font-size: 8px; color: #6366f1; margin-top: 2px; }

        .irp-scene-outer .ml1 { bottom: 350px; left: 30px; animation: irpLabelSlide 0.5s ease-out 1.3s both; }
        .irp-scene-outer .ml2 { bottom: 130px; left: 265px; animation: irpLabelSlide 0.5s ease-out 2.3s both; }
        .irp-scene-outer .ml3 { bottom: 350px; left: 505px; animation: irpLabelSlide 0.5s ease-out 3.3s both; }
        @keyframes irpLabelSlide { 0% { opacity: 0; transform: translateY(8px); } 100% { opacity: 1; transform: translateY(0); } }

        .irp-scene-outer .walker { position: absolute; bottom: 248px; left: 45px; animation: irpWalkAlong 8s ease-in-out 1.5s infinite; }
        .irp-scene-outer .walker .w-head { width: 16px; height: 16px; border-radius: 50%; background: #C68642; margin: 0 auto 2px; }
        .irp-scene-outer .walker .w-body { width: 12px; height: 16px; background: #6366f1; border-radius: 4px 4px 2px 2px; margin: 0 auto; }
        .irp-scene-outer .walker .w-legs { display: flex; gap: 2px; justify-content: center; }
        .irp-scene-outer .walker .w-leg { width: 4px; height: 10px; background: #312e81; border-radius: 2px; animation: irpLegWalk 0.4s ease-in-out infinite alternate; }
        .irp-scene-outer .walker .w-leg:nth-child(2) { animation-delay: 0.2s; }
        @keyframes irpLegWalk { 0% { transform: rotate(-15deg); } 100% { transform: rotate(15deg); } }
        @keyframes irpWalkAlong {
          0% { left: 45px; bottom: 248px; }
          15% { left: 130px; bottom: 215px; }
          30% { left: 220px; bottom: 185px; }
          45% { left: 310px; bottom: 165px; }
          55% { left: 390px; bottom: 190px; }
          65% { left: 460px; bottom: 220px; }
          80% { left: 540px; bottom: 250px; }
          90% { left: 620px; bottom: 230px; }
          95% { left: 680px; bottom: 200px; }
          100% { left: 700px; bottom: 175px; }
        }

        .irp-scene-outer .trophy { position: absolute; bottom: 280px; right: 50px; opacity: 0; animation: irpTrophyAppear 0.8s ease-out 3.5s both; }
        .irp-scene-outer .trophy-glow { width: 60px; height: 60px; border-radius: 50%; background: #f59e0b; display: flex; align-items: center; justify-content: center; box-shadow: 0 0 0 rgba(245,158,11,0.4); animation: irpTrophyGlow 2s ease-in-out 4s infinite; }
        .irp-scene-outer .trophy-icon { font-size: 28px; }
        @keyframes irpTrophyAppear { 0% { opacity: 0; transform: scale(0) rotate(-20deg); } 60% { transform: scale(1.15) rotate(5deg); } 100% { opacity: 1; transform: scale(1) rotate(0deg); } }
        @keyframes irpTrophyGlow { 0%, 100% { box-shadow: 0 0 0 0 rgba(245,158,11,0.4); } 50% { box-shadow: 0 0 0 15px rgba(245,158,11,0); } }

        .irp-scene-outer .stats { position: absolute; bottom: 25px; right: 30px; background: white; border-radius: 14px; padding: 14px 18px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); opacity: 0; animation: irpLabelSlide 0.6s ease-out 3.8s both; }
        .irp-scene-outer .stats .s-title { font-size: 10px; font-weight: 700; color: #1e1b4b; margin-bottom: 8px; }
        .irp-scene-outer .stat-row { margin-bottom: 6px; }
        .irp-scene-outer .stat-row .s-label { font-size: 8px; color: #64748b; margin-bottom: 2px; }
        .irp-scene-outer .stat-row .s-track { width: 130px; height: 5px; background: #e0e7ff; border-radius: 3px; overflow: hidden; }
        .irp-scene-outer .stat-row .s-fill { height: 100%; border-radius: 3px; animation: irpStatFill 1.5s ease-out 4s both; }
        .irp-scene-outer .stat-row:nth-child(2) .s-fill { background: #6366f1; width: 85%; }
        .irp-scene-outer .stat-row:nth-child(3) .s-fill { background: #818cf8; width: 65%; }
        .irp-scene-outer .stat-row:nth-child(4) .s-fill { background: #a5b4fc; width: 78%; }
        @keyframes irpStatFill { 0% { width: 0 !important; } }

        .irp-scene-outer .title-banner { position: absolute; top: 20px; left: 50%; transform: translateX(-50%); background: #4f46e5; padding: 10px 28px; border-radius: 12px; box-shadow: 0 4px 16px rgba(79,70,229,0.3); opacity: 0; animation: irpTitleDrop 0.6s ease-out 0.3s both; }
        .irp-scene-outer .title-banner span { font-size: 16px; font-weight: 700; color: white; }
        @keyframes irpTitleDrop { 0% { opacity: 0; transform: translateX(-50%) translateY(-15px); } 100% { opacity: 1; transform: translateX(-50%) translateY(0); } }

        .irp-scene-outer .flag { position: absolute; width: 3px; height: 14px; background: #818cf8; }
        .irp-scene-outer .flag::after { content: ''; position: absolute; top: 0; left: 3px; width: 10px; height: 7px; background: #6366f1; clip-path: polygon(0 0, 100% 50%, 0 100%); }
        .irp-scene-outer .flag1 { bottom: 230px; left: 170px; opacity: 0; animation: irpFlagPop 0.4s ease-out 1.6s both; }
        .irp-scene-outer .flag2 { bottom: 180px; left: 420px; opacity: 0; animation: irpFlagPop 0.4s ease-out 2.6s both; }
        @keyframes irpFlagPop { 0% { opacity: 0; transform: scaleY(0); } 100% { opacity: 0.7; transform: scaleY(1); } }

        .irp-scene-outer .grass { position: absolute; bottom: 95px; display: flex; gap: 3px; }
        .irp-scene-outer .grass span { width: 3px; height: 12px; background: #86efac; border-radius: 3px 3px 0 0; opacity: 0.5; }
        .irp-scene-outer .grass span:nth-child(2) { height: 16px; }
        .irp-scene-outer .grass span:nth-child(3) { height: 10px; }
        .irp-scene-outer .g1 { left: 80px; }
        .irp-scene-outer .g2 { left: 250px; }
        .irp-scene-outer .g3 { left: 450px; }
        .irp-scene-outer .g4 { left: 650px; }

        .irp-scene-outer .ground { position: absolute; bottom: 0; left: 0; right: 0; height: 100px; background: linear-gradient(180deg, #d9f99d33 0%, #bbf7d033 100%); }
        .irp-scene-outer .ground-line { position: absolute; bottom: 98px; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, transparent, #86efac44, transparent); }
      `}</style>

      <div style={{ width: SCENE_WIDTH * scale, height: SCENE_HEIGHT * scale, overflow: 'hidden', borderRadius: 20, flexShrink: 0 }}>
      <div className="scene" style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}>
        <div className="cloud cloud1"></div>
        <div className="cloud cloud2"></div>

        <div className="ground"></div>
        <div className="ground-line"></div>
        <div className="grass g1"><span></span><span></span><span></span></div>
        <div className="grass g2"><span></span><span></span><span></span></div>
        <div className="grass g3"><span></span><span></span><span></span></div>
        <div className="grass g4"><span></span><span></span><span></span></div>

        <div className="path-container">
          <svg viewBox="0 0 800 480" preserveAspectRatio="none">
            <path className="main-path" d="M80 200 Q160 200 200 260 Q240 320 320 300 Q400 280 420 230 Q440 180 520 200 Q600 220 640 180 Q680 140 720 160" />
            <path className="main-path-fill" d="M80 200 Q160 200 200 260 Q240 320 320 300 Q400 280 420 230 Q440 180 520 200 Q600 220 640 180 Q680 140 720 160" />
            <path className="path-dots" d="M80 200 Q160 200 200 260 Q240 320 320 300 Q400 280 420 230 Q440 180 520 200 Q600 220 640 180 Q680 140 720 160" />
          </svg>
        </div>

        <div className="flag flag1"></div>
        <div className="flag flag2"></div>

        <div className="milestone m1">
          <div className="circle"><div className="num">1</div><div className="sub">-10</div></div>
        </div>
        <div className="milestone m2">
          <div className="circle"><div className="num">11</div><div className="sub">-20</div></div>
        </div>
        <div className="milestone m3">
          <div className="circle"><div className="num">21</div><div className="sub">-30</div></div>
        </div>

        <div className="mile-label ml1">
          <div className="title">Fundamentals</div>
          <div className="desc">Self-intro, HR questions</div>
        </div>
        <div className="mile-label ml2">
          <div className="title">Intermediate</div>
          <div className="desc">Technical + Behavioral</div>
        </div>
        <div className="mile-label ml3">
          <div className="title">Advanced</div>
          <div className="desc">Competition rounds</div>
        </div>

        <div className="walker">
          <div className="w-head"></div>
          <div className="w-body"></div>
          <div className="w-legs">
            <div className="w-leg"></div>
            <div className="w-leg"></div>
          </div>
        </div>

        <div className="trophy">
          <div className="trophy-glow">
            <span className="trophy-icon">🏆</span>
          </div>
        </div>

        <div className="stats">
          <div className="s-title">Your Journey</div>
          <div className="stat-row"><div className="s-label">Communication</div><div className="s-track"><div className="s-fill"></div></div></div>
          <div className="stat-row"><div className="s-label">Technical</div><div className="s-track"><div className="s-fill"></div></div></div>
          <div className="stat-row"><div className="s-label">Confidence</div><div className="s-track"><div className="s-fill"></div></div></div>
        </div>

        <div className="title-banner"><span>30 Progressive Rounds</span></div>
      </div>
      </div>
    </div>
  );
}

export default function TestimonialsSection() {
  const sectionRef = useScrollReveal('.testimonials-reveal', { y: 30, stagger: 0.15 });

  return (
    <section ref={sectionRef} id="testimonials" data-testid="testimonials-section" className="pt-8 lg:pt-12 pb-20 lg:pb-28 bg-[#F5F7FA] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 mb-12">
        <div className="text-center">
          <p className="testimonials-reveal font-body text-sm font-semibold text-[#0056D2] uppercase tracking-wider mb-2">Student Success</p>
          <h2 className="testimonials-reveal font-heading font-bold text-3xl md:text-4xl text-[#1F1F1F]">
            The IRP Journey leads to SUCCESS
          </h2>
        </div>
      </div>

      <div className="testimonials-reveal">
        <IRPJourneyScene />
      </div>

      <div className="flex flex-wrap justify-center gap-3 mt-10 px-6">
        {['30 Rounds', 'Human Experts', 'Open Book', '1-on-1 Practice'].map(label => (
          <span
            key={label}
            className="testimonials-reveal inline-flex items-center gap-2 bg-[#4F46E5] text-white text-sm font-bold px-6 py-3 rounded-full"
          >
            <span>✓</span> {label}
          </span>
        ))}
      </div>
    </section>
  );
}
